import { relationFieldToRows } from '../../api/jenisPengukuranPerlengkapan';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';

export const FORM_QUESTION_ANSWER_TYPES = [
  'text',
  'number',
  'decimal',
  'boolean',
  'single',
  'multiple',
  'date',
  'file',
] as const;

export const FORM_QUESTION_LIST_FETCH_SIZE = 500;

export type FormQuestionAnswerType = (typeof FORM_QUESTION_ANSWER_TYPES)[number];

export type FormQuestionDraftRow = {
  key: string;
  documentId?: string;
  title: string;
  answerType: string;
  defaultValue: string;
  sortOrder: string;
  required: boolean;
};

export type FormQuestionGroup = {
  name: string;
  questions: DynamicEntityRow[];
};

export type FormQuestionGroupDraft = {
  formName: string;
  originalName: string;
  jenisPengukuranDocumentIds: string[];
  questions: FormQuestionDraftRow[];
  existingDocumentIds: string[];
};

export type FormQuestionGroupSavePlan = {
  toCreate: Record<string, unknown>[];
  toUpdate: Array<{ documentId: string; data: Record<string, unknown> }>;
  toDelete: string[];
};

let draftKeySeq = 0;

export function nextFormQuestionDraftKey(): string {
  draftKeySeq += 1;
  return `draft-${Date.now()}-${draftKeySeq}`;
}

export function formQuestionGroupKey(name: string): string {
  return `form:${name.trim() || '(tanpa nama)'}`;
}

export function emptyFormQuestionDraftRow(sortOrder = 0): FormQuestionDraftRow {
  return {
    key: nextFormQuestionDraftKey(),
    title: '',
    answerType: 'text',
    defaultValue: '',
    sortOrder: String(sortOrder),
    required: true,
  };
}

export function draftFromQuestionRow(row: DynamicEntityRow): FormQuestionDraftRow {
  return {
    key: row.documentId || nextFormQuestionDraftKey(),
    documentId: row.documentId || undefined,
    title: String(row.title ?? ''),
    answerType: String(row.answer_type ?? 'text'),
    defaultValue: String(row.default_value ?? ''),
    sortOrder: row.sort_order == null ? '0' : String(row.sort_order),
    required: row.required == null ? true : Boolean(row.required),
  };
}

export function uniqueJenisPengukuranDocumentIds(questions: DynamicEntityRow[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const question of questions) {
    for (const rel of relationFieldToRows(question.jenis_pengukuran)) {
      const id = String(rel.documentId ?? '').trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

export function groupFormQuestionsByName(rows: DynamicEntityRow[]): FormQuestionGroup[] {
  const map = new Map<string, DynamicEntityRow[]>();
  for (const row of rows) {
    const name = String(row.form_question_name ?? '').trim() || '(tanpa nama)';
    const list = map.get(name) ?? [];
    list.push(row);
    map.set(name, list);
  }
  const groups: FormQuestionGroup[] = [];
  for (const [name, questions] of map) {
    questions.sort((a, b) => {
      const sa = Number(a.sort_order ?? 0);
      const sb = Number(b.sort_order ?? 0);
      if (sa !== sb) return sa - sb;
      return String(a.title ?? '').localeCompare(String(b.title ?? ''), 'id');
    });
    groups.push({ name, questions });
  }
  return groups.sort((a, b) => a.name.localeCompare(b.name, 'id'));
}

export function questionsForFormName(rows: DynamicEntityRow[], formName: string): DynamicEntityRow[] {
  const wanted = formName.trim().toLowerCase();
  return rows
    .filter((row) => String(row.form_question_name ?? '').trim().toLowerCase() === wanted)
    .sort((a, b) => {
      const sa = Number(a.sort_order ?? 0);
      const sb = Number(b.sort_order ?? 0);
      if (sa !== sb) return sa - sb;
      return String(a.title ?? '').localeCompare(String(b.title ?? ''), 'id');
    });
}

export function paginateFormQuestionGroups(
  groups: FormQuestionGroup[],
  page: number,
  pageSize: number,
): { rows: FormQuestionGroup[]; total: number } {
  const size = Math.max(1, pageSize);
  const start = Math.max(0, page) * size;
  return { rows: groups.slice(start, start + size), total: groups.length };
}

export function formQuestionNameTaken(
  name: string,
  existingNames: string[],
  ignoreName?: string,
): boolean {
  const n = name.trim().toLowerCase();
  if (!n) return false;
  const ignore = (ignoreName ?? '').trim().toLowerCase();
  return existingNames.some((raw) => {
    const t = raw.trim().toLowerCase();
    return Boolean(t) && t === n && t !== ignore;
  });
}

export function documentIdsInFormQuestionGroups(
  selectedGroupKeys: string[],
  groups: FormQuestionGroup[],
): string[] {
  const keySet = new Set(selectedGroupKeys.map((k) => String(k ?? '').trim()).filter(Boolean));
  if (!keySet.size) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const group of groups) {
    if (!keySet.has(formQuestionGroupKey(group.name))) continue;
    for (const question of group.questions) {
      const id = String(question.documentId ?? '').trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

export function draftFromFormQuestionGroup(
  formName: string,
  questions: DynamicEntityRow[],
): FormQuestionGroupDraft {
  const name = formName.trim();
  const drafts = questions.map(draftFromQuestionRow);
  return {
    formName: name,
    originalName: name,
    jenisPengukuranDocumentIds: uniqueJenisPengukuranDocumentIds(questions),
    questions: drafts.length ? drafts : [emptyFormQuestionDraftRow(0)],
    existingDocumentIds: questions.map((q) => q.documentId).filter(Boolean),
  };
}

export function emptyFormQuestionGroupDraft(): FormQuestionGroupDraft {
  return {
    formName: '',
    originalName: '',
    jenisPengukuranDocumentIds: [],
    questions: [emptyFormQuestionDraftRow(0)],
    existingDocumentIds: [],
  };
}

function isCompletelyEmptyDraft(row: FormQuestionDraftRow): boolean {
  return (
    !row.documentId &&
    !row.title.trim() &&
    !String(row.defaultValue ?? '').trim() &&
    (row.answerType === 'text' || !row.answerType)
  );
}

export function buildFormQuestionWritePayload(
  formName: string,
  row: FormQuestionDraftRow,
  rowLabel: string,
  jenisPengukuranDocumentIds: string[],
): Record<string, unknown> {
  const title = row.title.trim();
  if (!title) throw new Error(`${rowLabel}: judul wajib diisi.`);
  const sortRaw = row.sortOrder.trim();
  const sortOrder = sortRaw === '' ? 0 : Number(sortRaw);
  if (!Number.isFinite(sortOrder)) throw new Error(`${rowLabel}: urutan tidak valid.`);
  const answerType = row.answerType.trim();
  if (!FORM_QUESTION_ANSWER_TYPES.includes(answerType as FormQuestionAnswerType)) {
    throw new Error(`${rowLabel}: tipe jawaban tidak valid.`);
  }
  return {
    form_question_name: formName.trim(),
    title,
    answer_type: answerType,
    default_value: String(row.defaultValue ?? '').trim(),
    required: row.required,
    sort_order: sortOrder,
    jenis_pengukuran: { set: jenisPengukuranDocumentIds.map((id) => id.trim()).filter(Boolean) },
  };
}

export function planFormQuestionGroupSave(draft: FormQuestionGroupDraft): FormQuestionGroupSavePlan {
  const formName = draft.formName.trim();
  if (!formName) throw new Error('Nama form wajib diisi.');

  const rows = draft.questions.filter((row) => !isCompletelyEmptyDraft(row));
  if (!rows.length) throw new Error('Minimal satu pertanyaan dengan judul.');

  rows.forEach((row, index) => {
    if (!row.title.trim()) {
      throw new Error(`Baris ${index + 1}: judul wajib diisi.`);
    }
  });

  const keptIds = new Set(rows.map((row) => row.documentId).filter((id): id is string => Boolean(id)));
  const toDelete = draft.existingDocumentIds.filter((id) => !keptIds.has(id));
  const toCreate: Record<string, unknown>[] = [];
  const toUpdate: Array<{ documentId: string; data: Record<string, unknown> }> = [];

  const jenisPengukuranDocumentIds = draft.jenisPengukuranDocumentIds.map((id) => id.trim()).filter(Boolean);

  rows.forEach((row, index) => {
    const data = buildFormQuestionWritePayload(formName, row, `Baris ${index + 1}`, jenisPengukuranDocumentIds);
    if (row.documentId) toUpdate.push({ documentId: row.documentId, data });
    else toCreate.push(data);
  });

  return { toCreate, toUpdate, toDelete };
}

export function formQuestionRowIsPersisted(row: Pick<FormQuestionDraftRow, 'documentId'>): boolean {
  return Boolean(row.documentId?.trim());
}

export function formQuestionGroupHasPersistedRow(draft: Pick<FormQuestionGroupDraft, 'questions'>): boolean {
  return draft.questions.some(formQuestionRowIsPersisted);
}

export function questionDocumentIds(questions: DynamicEntityRow[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const question of questions) {
    const id = String(question.documentId ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function toggleSelectedId(ids: string[], documentId: string, selected: boolean): string[] {
  const id = documentId.trim();
  if (!id) return ids;
  const has = ids.includes(id);
  if (selected) return has ? ids : [...ids, id];
  return has ? ids.filter((item) => item !== id) : ids;
}

export function toggleSelectedGroup(ids: string[], groupIds: string[], selected: boolean): string[] {
  const wanted = new Set(groupIds.map((id) => id.trim()).filter(Boolean));
  if (!wanted.size) return ids;
  if (selected) {
    const next = [...ids];
    const seen = new Set(ids);
    for (const id of wanted) {
      if (seen.has(id)) continue;
      seen.add(id);
      next.push(id);
    }
    return next;
  }
  return ids.filter((id) => !wanted.has(id));
}

export function selectedCountInGroup(ids: string[], groupIds: string[]): number {
  const selected = new Set(ids);
  return groupIds.filter((id) => selected.has(id)).length;
}
